/**
 * Protobuf Decoder Utilities
 *
 * Provides helper functions to decode protobuf messages from ArrayBuffer
 * using protobufjs runtime.
 */

import { Root, Type, load } from 'protobufjs'
import { logger } from '@/utils/logger'
import type { UnknownElementDecodeResult } from './types/unknown-element-decode-result'
import { LruCache } from './helpers/lru-cache'
import { hasPathCommands, hasUsableBaseGeometry } from './helpers/element-shape-probe'
const PROTO_PACKAGE = 'schema'

const PROTOBUF_CACHE_MAX_ENTRIES = 1000
const arrayBufferCache = new LruCache<string, ArrayBuffer>(PROTOBUF_CACHE_MAX_ENTRIES)
const decodedCache = new LruCache<string, unknown>(PROTOBUF_CACHE_MAX_ENTRIES)

/**
 * Element kind to protobuf type name mapping
 * Maps kindId to the corresponding protobuf message type
 */
export const ELEMENT_KIND_TO_TYPE: Record<number, string> = {
  1: 'RectangleElement',
  2: 'IrregularPolygonElement',
  3: 'TextElement',
  4: 'ImageElement',
  5: 'PageNumberElement',
  6: 'TextOnPathElement',
  7: 'GroupElement',
  8: 'OvalElement',
  9: 'PolylineElement',
  10: 'RegularPolygonElement',
}

/**
 * Get protobuf type name from kindId
 */
export function getElementTypeName(kindId: number): string {
  const typeName = ELEMENT_KIND_TO_TYPE[kindId]

  if (!typeName) {
    throw new ProtobufDecodeError(`Unsupported element kind: ${kindId}`, undefined)
  }

  return typeName
}

/**
 * Custom error for protobuf decoding failures
 */
export class ProtobufDecodeError extends Error {
  constructor(
    message: string,
    public readonly typeName?: string,
    public readonly cause?: unknown
  ) {
    super(message)
    this.name = 'ProtobufDecodeError'
  }
}

/**
 * Singleton protobuf root instance
 */
let protobufRoot: Root | null = null
let isInitialized = false
let initializationPromise: Promise<void> | null = null

/**
 * Initialize the protobuf decoder by loading the schema
 */
export async function initializeDecoder(): Promise<void> {
  if (isInitialized && protobufRoot) {
    return
  }

  // If initialization is already in progress, wait for it
  if (initializationPromise) {
    return initializationPromise
  }

  initializationPromise = (async () => {
    try {
      // Try loading from public directory (accessible via HTTP in browser)
      // The proto file should be copied to public/proto/ during build
      const protoPaths = [
        '/proto/schema.proto', // Public path (copied by proto:generate)
        '/src/proto/schema.proto', // Alternative path
      ]

      let lastError: Error | null = null

      for (const protoPath of protoPaths) {
        try {
          protobufRoot = await load(protoPath)
          isInitialized = true
          logger.debug(`Protobuf decoder initialized from: ${protoPath}`)
          return
        } catch (error) {
          lastError = error as Error
          logger.debug(`Failed to load from ${protoPath}:`, error)
          continue
        }
      }

      // If HTTP paths failed, try loading the proto file content directly
      // This works if the proto file is imported as text
      try {
        // Try to fetch from the public directory
        const response = await fetch('/proto/schema.proto')

        if (response.ok) {
          const protoText = await response.text()

          // Use load with the proto text as a data URL or parse it
          // Create a blob URL and load from it
          const blob = new Blob([protoText], { type: 'text/plain' })
          const blobUrl = URL.createObjectURL(blob)

          try {
            protobufRoot = await load(blobUrl)
            isInitialized = true
            logger.debug('Protobuf decoder initialized from fetch')
            URL.revokeObjectURL(blobUrl)
            return
          } catch (blobError) {
            URL.revokeObjectURL(blobUrl)
            throw blobError
          }
        }
      } catch {
        // Ignore fetch errors
      }

      throw (
        lastError ||
        new Error('All proto file paths failed. Make sure to run "npm run proto:generate" first.')
      )
    } catch (error) {
      logger.error('Failed to initialize protobuf decoder:', error)
      throw new ProtobufDecodeError(
        'Failed to load protobuf schema. Make sure schema.proto is accessible. Run "npm run proto:generate" first.',
        undefined,
        error
      )
    } finally {
      initializationPromise = null
    }
  })()

  return initializationPromise
}

/**
 * Ensure decoder is initialized
 */
function ensureInitialized(): void {
  if (!isInitialized || !protobufRoot) {
    throw new ProtobufDecodeError(
      'Protobuf decoder not initialized. Call initializeDecoder() first.'
    )
  }
}

/**
 * Get a protobuf type by name
 */
function getType(typeName: string): Type {
  ensureInitialized()

  const fullTypeName = `${PROTO_PACKAGE}.${typeName}`
  const messageType = protobufRoot!.lookupType(fullTypeName) as Type

  if (!messageType) {
    throw new ProtobufDecodeError(`Protobuf type not found: ${fullTypeName}`, typeName)
  }

  return messageType
}

/**
 * Decode a protobuf message from ArrayBuffer
 */
function decodeMessage<T = unknown>(typeName: string, buffer: ArrayBuffer): T {
  try {
    // Validate buffer
    if (!buffer || buffer.byteLength === 0) {
      throw new Error('Empty or invalid buffer provided')
    }

    const messageType = getType(typeName)
    const uint8Array = new Uint8Array(buffer)

    // Decode the binary data
    let message

    try {
      message = messageType.decode(uint8Array)
    } catch (decodeError) {
      // Log first few bytes for debugging
      const preview = Array.from(uint8Array.slice(0, Math.min(20, uint8Array.length)))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join(' ')

      logger.error(
        `Failed to decode ${typeName}. Buffer preview (hex): ${preview}${uint8Array.length > 20 ? '...' : ''}`
      )
      throw decodeError
    }

    // Convert to plain JavaScript object
    const object = messageType.toObject(message, {
      longs: String, // Convert longs to strings
      enums: String, // Convert enum values to strings
      bytes: String, // Convert bytes to base64 strings
      defaults: false, // Don't include default values
      arrays: true, // Include empty arrays
      objects: true, // Include empty objects
      oneofs: true, // Include oneof fields
    })

    return object as T
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)

    logger.error(`Error decoding ${typeName}:`, error)
    logger.error(`Error details: ${errorMessage}`)
    throw new ProtobufDecodeError(`Failed to decode ${typeName}: ${errorMessage}`, typeName, error)
  }
}

/**
 * Fetch and decode a protobuf message from a URL, with in-memory caching.
 *
 * Important: cache by a *stable* key (e.g. objectId + versionId), not by signedUrl.
 * Signed URLs change over time even when the underlying object version does not.
 */
export async function fetchAndDecodeCached<T = unknown>(
  typeName: string,
  url: string,
  cacheKey: string
): Promise<T> {
  ensureInitialized()

  const cachedDecoded = decodedCache.get(cacheKey)

  if (cachedDecoded !== undefined) {
    return cachedDecoded as T
  }

  const cachedBuffer = arrayBufferCache.get(cacheKey)

  if (cachedBuffer) {
    const decoded = decodeMessage<T>(typeName, cachedBuffer)

    decodedCache.set(cacheKey, decoded as unknown)
    return decoded
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/x-protobuf, */*',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch protobuf data: ${response.status} ${response.statusText}`)
  }

  const arrayBuffer = await response.arrayBuffer()

  arrayBufferCache.set(cacheKey, arrayBuffer)

  const decoded = decodeMessage<T>(typeName, arrayBuffer)

  decodedCache.set(cacheKey, decoded as unknown)
  return decoded
}

/**
 * Best-effort decode when an element kindId is missing.
 *
 * Strategy:
 * - Try a short list of likely element types (Rectangle/IrregularPolygon first).
 * - Pick the first decode that yields usable `base.{width,height}` geometry.
 * - Infer kindId=2 when path commands exist, else kindId=1.
 */
export async function decodeUnknownElementFromBuffer(
  buffer: ArrayBuffer
): Promise<UnknownElementDecodeResult> {
  ensureInitialized()

  const candidates: string[] = [
    'IrregularPolygonElement',
    'RectangleElement',
    'ImageElement',
    'TextElement',
  ]
  let lastError: unknown = null

  for (const typeName of candidates) {
    try {
      const decoded = decodeMessage(typeName, buffer)

      if (!hasUsableBaseGeometry(decoded)) {
        continue
      }

      const inferredKindId = hasPathCommands(decoded) ? 2 : 1

      return { decoded, inferredKindId, inferredTypeName: typeName }
    } catch (err) {
      lastError = err
      continue
    }
  }

  throw new ProtobufDecodeError(
    'Failed to decode unknown element: no candidate type matched expected geometry',
    'UnknownElement',
    lastError
  )
}
